import type { CommissionReportRow } from "@/lib/commissions/types";

const round2 = (n: number) => Math.round(n * 100) / 100;

export function summarize(rows: CommissionReportRow[]) {
  const people = new Map<string, { id: string; name: string; total: number; groups: Map<string, number> }>();
  let total = 0;
  for (const r of rows) {
    total += r.amount;
    const p = people.get(r.professional_id) ?? { id: r.professional_id, name: r.professional_name, total: 0, groups: new Map() };
    p.total += r.amount;
    p.groups.set(r.group_name, (p.groups.get(r.group_name) ?? 0) + r.amount);
    people.set(r.professional_id, p);
  }
  return {
    total: round2(total),
    people: [...people.values()]
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
      .map((p) => ({
        id: p.id, name: p.name, total: round2(p.total),
        groups: [...p.groups.entries()].sort(([a], [b]) => a.localeCompare(b, "pt-BR")).map(([name, amount]) => ({ name, amount: round2(amount) })),
      })),
  };
}

/** Neutralises spreadsheet formula injection in free-text cells (names typed by users). */
export function csvSafeText(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

export function toCsvRows(rows: CommissionReportRow[], t: (k: string) => string, locale: string): (string | number)[][] {
  const money = (n: number) => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const pct = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
  const date = (iso: string) =>
    new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo" }).format(new Date(iso));
  return [
    ["date", "sale", "professional", "item", "group", "base", "percent", "amount"].map((k) => t(`report.columns.${k}`)),
    ...rows.map((r) => [date(r.occurred_at), csvSafeText(r.sale_number), csvSafeText(r.professional_name), csvSafeText(r.item_name), csvSafeText(r.group_name || t("catalog.models.noGroup")), money(r.base_amount), pct(r.percent), money(r.amount)]),
  ];
}
